import { signal } from '@angular/core';
import { DataFieldReference, DataFieldUsage, DataModelDto, DataSchemaReference, DataSchemaType, DataSourceDto, DataSourceReference, DataSourceType, DataTableReference, ProductDataSourceReference, RelationshipCardinality, ResultSetDataSourceReference, SchemaFieldTargetDto, SchemaTargetDto, UserDatasetDataSourceReference } from 'src/app/services/api.services';
import { isNullOrUndefined, ProductName } from 'src/app/shared/utils';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { CustomFieldSyntaxVisitor } from '../visuals/customFields/CustomFieldSyntaxVisitor';
import { CustomFields } from '../visuals/customFields/CustomFields';
import { ElementCollection } from './Collections';
import { DataField } from './DataField';
import { DataRelationship, DataRelationshipKey } from './DataRelationship';
import { DataSchemaItem } from './DataSchemaItem';
import { DataSource } from './DataSource';
import { DataTable } from './DataTable';
import { DataRelationshipMapping } from './DataRelationshipMappings';

export interface DataModelOptions {
  readonly relationshipMapping?: DataRelationshipMapping;
}

export class DataModel {
  readonly createdFromDto: DataModelDto;

  base?: DataModel;
  readonly extensions: DataModel[] = [];

  readonly sources = new DataModel.SourceCollection(this);
  readonly relationships = new DataModel.RelationshipCollection(this);

  readonly tables = new DataModel.TableLookup(this);
  readonly fields = new DataModel.FieldLookup(this);
  readonly schemas = new DataModel.SchemaLookup(this);
  readonly schemaItems = new DataModel.SchemaItemLookup(this);

  readonly relationshipMapping: DataRelationshipMapping;

  constructor();
  constructor(options: DataModelOptions);
  constructor(dto: DataModelDto, options?: DataModelOptions);
  constructor(base: DataModel, options?: DataModelOptions);
  constructor(arg1?: DataModelDto | DataModel | DataModelOptions, arg2?: DataModelOptions) {
    let value: DataModelDto | DataModel;
    let options: DataModelOptions;

    if (arg1 instanceof DataModel || (!isNullOrUndefined(arg1) && 'sources' in arg1)) {
      value = arg1;
      options = arg2;
    }
    else {
      options = <DataModelOptions><any>arg1;
    }

    let relationshipMapping = options?.relationshipMapping;

    if (value instanceof DataModel) {
      this.base = value;

      if (!relationshipMapping)
        relationshipMapping = value.relationshipMapping;
    }
    else {
      let dto = value;

      let sources = dto?.sources?.map(s => ({
        dto: s,
        source: this.sources.add(s.reference)
      })) ?? [];

      sources.forEach(({ dto, source }) => source.setFromDto1(dto));
      sources.forEach(({ dto, source }) => source.setFromDto2(dto));

      dto?.relationships.forEach(r => {
        this.relationships.set(
          this.fields.get(r.from),
          this.fields.get(r.to),
          r.cardinality,
          r.nonNull);
      });
    }

    this.relationshipMapping =
      (this.base || this.extensions?.length)
        ? new DataRelationshipMapping(
          { products: [], timestamp: 0 },
          relationshipMapping,
          this.extensions?.map(x => x.relationshipMapping).filter(x => !x))
        : (relationshipMapping ?? DataRelationshipMapping.empty);
  }

  readonly customFieldIdentifierLookup = new DataModel.CustomFieldIdentifierLookup(this);

  toDto(): DataModelDto {
    return {
      sources: [...this.sources.map(x => x.toDto())],
      relationships: [...this.relationships.map(x => x.toDto())]
    };
  }
}

export namespace DataModel {
  export class SourceCollection extends ElementCollection<DataSourceReference, DataSource> {
    constructor(public readonly m: DataModel) {
      super();
    }

    readonly products = new class {
      constructor(public readonly s: SourceCollection) { }

      find(product: ProductName) { return this.s.find(this.key(product)); }
      get(product: ProductName) { return this.s.get(this.key(product)); }

      private key(product: ProductName): ProductDataSourceReference {
        return {
          type: DataSourceType.Product,
          product
        };
      }

      *values(): Iterable<DataSource> {
        for (let ds of this.s.values()) {
          if (ds.type != DataSourceType.Product)
            continue;

          yield ds;
        }
      }

      *[Symbol.iterator](): Iterator<DataSource> {
        for (let item of [...this.values()])
          yield item;
      }
    }(this);

    readonly userDatasets = new class {
      constructor(public readonly s: SourceCollection) { }

      find(userDatasetId: number) { return this.s.find(this.key(userDatasetId)); }
      get(userDatasetId: number) { return this.s.get(this.key(userDatasetId)); }

      private key(userDatasetId: number): UserDatasetDataSourceReference {
        return {
          type: DataSourceType.UserDataset,
          userDatasetId
        };
      }

      *values(): Iterable<DataSource> {
        for (let ds of this.s.values()) {
          if (ds.type != DataSourceType.UserDataset)
            continue;

          yield ds;
        }
      }

      *[Symbol.iterator](): Iterator<DataSource> {
        for (let item of [...this.values()])
          yield item;
      }
    }(this); 

    add(reference: DataSourceReference): DataSource {
      let source = new DataSource(this.m, reference);
      super._add(source);
      return source;
    }

    addFromDto(dto: DataSourceDto): DataSource {
      let source = new DataSource(this.m, dto.reference);
      source.setFromDto1(dto);
      source.setFromDto2(dto);
      return source;
    }

    getOrAdd(reference: ProductDataSourceReference): DataSource;
    getOrAdd(reference: ResultSetDataSourceReference): DataSource;
    getOrAdd(reference: UserDatasetDataSourceReference): DataSource;
    getOrAdd(reference: DataSourceReference): DataSource {
      let source = this.find(reference);
      if (source)
        return source;

      return this.add(reference);
    }

    remove(source: DataSource) {
      return this._remove(source);
    }

    protected normalizeKey(key: DataSourceReference) {
      switch (key.type) {
        case DataSourceType.Product: {
          let reference = <ProductDataSourceReference>key;
          return `Product/${reference.product.toLowerCase()}`;
        }

        case DataSourceType.UserDataset: {
          let reference = <UserDatasetDataSourceReference>key;
          return `UserDataset/${reference.userDatasetId}`;
        }

        case DataSourceType.ResultSet: {
          let reference = <ResultSetDataSourceReference>key;
          return `ResultSet/${reference.resultSetId}`;
        }
      }

      throw new Error(`Unsupported DataSourceReference`, { cause: key });
    }

    protected getKey(item: DataSource): DataSourceReference {
      return item.reference;
    }

    protected getBase() { return this.m.base?.sources; }
    protected getExtensions() { return this.m.base?.extensions.map(x => x.sources) ?? []; }
  }

  export class RelationshipCollection extends ElementCollection<DataRelationshipKey, DataRelationship> {
    constructor(public readonly m: DataModel) {
      super();
    }

    set(
      from: DataField,
      to: DataField,
      cardinality: RelationshipCardinality,
      nonNull: boolean): DataRelationship {
      let key: DataRelationshipKey = {
        fromTable: from.table,
        toTable: to.table,
      };

      let relationship = this.find(key);
      if (!relationship) {
        relationship = new DataRelationship(from, to, cardinality, nonNull);
        this._add(relationship);
      }
      else {
        relationship.cardinality = cardinality;
        relationship.nonNull = nonNull;
      }

      return relationship;
    }

    *of(table: DataTable): Iterator<DataRelationship> {
      for (let r of this.values()) {
        if (r.from.table == table || r.to.table === table) {
          yield r;
        }
      }
    }

    findBetween(table1: DataTable | DataTableReference, table2: DataTable | DataTableReference) {
      return this.find({
        fromTable: table1 instanceof DataTable ? table1 : this.m.tables.get(table1),
        toTable: table2 instanceof DataTable ? table2 : this.m.tables.get(table2)
      });
    }

    getBetween(table1: DataTable | DataTableReference, table2: DataTable | DataTableReference) {
      return this.get({
        fromTable: table1 instanceof DataTable ? table1 : this.m.tables.get(table1),
        toTable: table2 instanceof DataTable ? table2 : this.m.tables.get(table2)
      });
    }

    remove(relationship: DataRelationship) {
      return this._remove(relationship);
    }


    protected normalizeKey(key: DataRelationshipKey) {
      return JSON.stringify([key.fromTable.reference, key.toTable.reference]);
    }

    protected getKey(item: DataRelationship): DataRelationshipKey {
      return item.key;
    }

    protected getBase() { return this.m.base?.relationships; }
    protected getExtensions() { return this.m.base?.extensions.map(x => x.relationships) ?? []; }
  }

  export class TableLookup {
    constructor(public readonly m: DataModel) { }

    find(table: DataTableReference) {
      return this.m.sources.find(table.source)?.tables.find(table.table);
    }

    get(table: DataTableReference) {
      let item = this.find(table);
      if (!item)
        throw new Error(`Could not find table.`, { cause: table });

      return item;
    }
  }

  export class FieldLookup {
    constructor(public readonly m: DataModel) { }

    find(field: DataFieldReference) {
      return this.m.tables.find(field.table)?.fields.find(...field.path);
    }

    get(field: DataFieldReference) {
      let item = this.find(field);
      if (!item)
        throw new Error(`Could not find field.`, { cause: field });

      return item;
    }
  }

  export class SchemaLookup {
    constructor(public readonly m: DataModel) { }

    find(source: DataSourceReference, schema: DataSchemaReference) {
      return this.m.sources.find(source)?.schemas.find(schema);
    }

    get(source: DataSourceReference, schema: DataSchemaReference) {
      let s = this.find(source, schema);
      if (!s)
        throw new Error(`Could not find schema.`, { cause: { source, schema } });

      return s;
    }

    product(product: string) {
      let lookup = this;
      let sourceReference = { type: DataSourceType.Product, product };

      return {
        find(schemaName: string) {
          return lookup.find(sourceReference, {
            type: DataSchemaType.Core,
            name: schemaName
          });
        },

        get(schemaName: string) {
          return lookup.get(sourceReference, {
            type: DataSchemaType.Core,
            name: schemaName
          });
        }
      };
    }

    userDataset(userDatasetId: number) {
      let lookup = this;
      let sourceReference = { type: DataSourceType.UserDataset, userDatasetId };

      return {
        find(schemaName: string) {
          return lookup.find(sourceReference, {
            type: DataSchemaType.Core,
            name: schemaName
          });
        },

        get(schemaName: string) {
          return lookup.get(sourceReference, {
            type: DataSchemaType.Core,
            name: schemaName
          });
        }
      };
    }

    resultSet(resultSetId: string) {
      let lookup = this;
      let sourceReference = { type: DataSourceType.ResultSet, resultSetId };

      return {
        find(schemaName: string) {
          return lookup.find(sourceReference, {
            type: DataSchemaType.Core,
            name: schemaName
          });
        },

        get(schemaName: string) {
          return lookup.get(sourceReference, {
            type: DataSchemaType.Core,
            name: schemaName
          });
        }
      };
    }

    findByTarget(target: SchemaTargetDto) {
      if (SchemaUtils.isCore(target)) {
        return this.product(target.product)?.find(target.name);
      }

      if (SchemaUtils.isUserDataset(target)) {
        return this.userDataset(target.userDatasetId)?.find(target.name);
      }

      if (SchemaUtils.isResultSet(target)) {
        return this.resultSet(target.resultSetId)?.find(target.name);
      }

      return null;
    }

    getByTarget(target: SchemaTargetDto) {
      let s = this.findByTarget(target);
      if (!s)
        throw new Error(`Could not find schema.`, { cause: target });

      return s;
    }
  }

  export class SchemaItemLookup {
    constructor(public readonly m: DataModel) { }

    find(source: DataSourceReference, schema: DataSchemaReference, group: string, ...path: string[]) {
      return this.m.schemas.find(source, schema)?.itemGroups.find(group)?.items.find(...path);
    }

    get(source: DataSourceReference, schema: DataSchemaReference, group: string, ...path: string[]) {
      let item = this.find(source, schema, group, ...path);
      if (!item)
        throw new Error(`Could not find schema item.`, { cause: { source, schema, group, path } });

      return item;
    }

    findByTarget(target: SchemaFieldTargetDto) {
      let path = [target.field];

      if (target.childField)
        path.push(target.childField);

      return this.m.schemas.findByTarget(target.schema)?.itemGroups.find(target.group)?.items.find(...path);
    }

    getByTarget(target: SchemaFieldTargetDto) {
      let item = this.findByTarget(target);
      if (!item)
        throw new Error(`Could not find schema item.`, { cause: target });

      return item;
    }
  }


  interface CustomFieldIdentifierLookupEntry {
    readonly item: DataSchemaItem;
    readonly field: CustomFields.Field;
  }

  export class CustomFieldIdentifierLookup {
    private map: Map<string, CustomFieldIdentifierLookupEntry>;
    
    constructor(public readonly m: DataModel) { }

    readonly resolver: CustomFieldSyntaxVisitor.FieldResolver = (identifier) => {
      this.ensureMap();

      let name = identifier.toString().toLowerCase();
      let item = this.map.get(name);
      if (!item)
        return null;

      return item.field;
    };

    private createMap() {
      let map = new Map<string, CustomFieldIdentifierLookupEntry>();

      for (let s of this.m.sources) {
        for (let ss of s.schemas) {
          for (let g of ss.itemGroups) {
            for (let i of g.items) {
              visitItem(i);
            }
          }
        }
      }

      return map;


      function visitItem(item: DataSchemaItem) {
        let target = signal(
          item.getDefaultUsage() == DataFieldUsage.Measure && !item.field.isValueAggregated
            ? item.createVisualFieldTarget(item.getDefaultAggregation())
            : item.createVisualFieldTarget(null));

        map.set(`[${item.getIdentifierName().toLowerCase()}]`, {
          item,
          field: target,
        });

        for (let c of item.children)
          visitItem(c);
      }
    }

    private ensureMap() {
      if (!this.map)
        this.map = this.createMap();
    }
  }
}
